import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Compass, Building2, Package, Briefcase, Globe2, TrendingUp, Gem } from "lucide-react";
import { LogoEmblem } from "@/components/Logo";

const FLOATERS = [
  { Icon: Building2, x: "7%", y: "20%", d: 0, dur: 8, s: 46 },
  { Icon: Globe2, x: "88%", y: "16%", d: 1.2, dur: 10, s: 42 },
  { Icon: Package, x: "12%", y: "70%", d: 0.6, dur: 9, s: 40 },
  { Icon: Briefcase, x: "91%", y: "66%", d: 1.8, dur: 11, s: 38 },
  { Icon: TrendingUp, x: "48%", y: "10%", d: 0.9, dur: 8.5, s: 36 },
  { Icon: Gem, x: "72%", y: "84%", d: 1.4, dur: 10.5, s: 34 },
];

export default function Hero({ hero, eyebrow, strip }) {
  const navigate = useNavigate();
  const sub = hero?.sub || "Connecting investors and businesses with carefully sourced property, commercial, material and equipment opportunities across selected markets.";

  return (
    <section data-testid="hero" className="grain relative flex min-h-screen items-center overflow-hidden bg-obsidian">
      {/* ambient glows */}
      <div className="pointer-events-none absolute right-[-10%] top-1/4 h-[620px] w-[620px] rounded-full" style={{ background: "radial-gradient(circle, rgba(8,44,58,0.55) 0%, transparent 65%)" }} />
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-[440px] w-[440px] -translate-x-1/2 rounded-full" style={{ background: "radial-gradient(circle, rgba(201,162,39,0.06) 0%, transparent 70%)" }} />

      {/* floating sourcing objects */}
      {FLOATERS.map(({ Icon, x, y, d, dur, s }, i) => (
        <motion.div key={i} className="pointer-events-none absolute hidden text-gold/45 sm:block" style={{ left: x, top: y }}
          animate={{ y: [0, -26, 0], opacity: [0.3, 0.65, 0.3] }}
          transition={{ duration: dur, delay: d, repeat: Infinity, ease: "easeInOut" }}>
          <Icon size={s} strokeWidth={1.2} />
        </motion.div>
      ))}
      {/* faint connecting node lines */}
      <svg className="pointer-events-none absolute inset-0 hidden h-full w-full sm:block" preserveAspectRatio="none">
        <line x1="10%" y1="24%" x2="50%" y2="14%" stroke="#C9A227" strokeWidth="0.5" opacity="0.08" />
        <line x1="50%" y1="14%" x2="86%" y2="20%" stroke="#C9A227" strokeWidth="0.5" opacity="0.08" />
        <line x1="16%" y1="72%" x2="70%" y2="82%" stroke="#C9A227" strokeWidth="0.5" opacity="0.08" />
      </svg>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-12 px-6 pt-28 pb-16 lg:grid-cols-[1.1fr_0.9fr] lg:px-10">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
          <div className="flex items-center gap-3">
            <span className="h-px w-10 bg-gold/60" />
            <span className="font-sans text-xs tracking-luxe uppercase text-gold">{eyebrow || "Global Sourcing House"}</span>
          </div>

          <h1 className="mt-7 font-serif text-4xl leading-[1.05] tracking-tight text-warmwhite sm:text-5xl lg:text-6xl">
            <motion.span className="inline-block" animate={{ y: [0, -10, 0] }} transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}>
              Exclusive Deals.
            </motion.span>
            <br />
            <motion.span className="inline-block text-gradient-gold italic" animate={{ y: [0, -10, 0] }} transition={{ duration: 2.6, delay: 0.35, repeat: Infinity, ease: "easeInOut" }}>
              Strategically Sourced.
            </motion.span>
          </h1>

          <p className="mt-7 max-w-xl font-sans text-base font-light leading-relaxed text-softgrey">{sub}</p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button onClick={() => navigate("/opportunities")} data-testid="hero-explore-cta"
                    className="group flex items-center gap-2 bg-gold px-7 py-3.5 font-sans text-sm font-medium tracking-wide text-obsidian transition-all hover:brightness-110">
              Explore Opportunities <ArrowUpRight size={17} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button onClick={() => navigate("/bespoke-sourcing")} data-testid="hero-bespoke-cta"
                    className="group flex items-center gap-2 border border-gold/40 px-7 py-3.5 font-sans text-sm tracking-wide text-warmwhite transition-colors hover:bg-gold/10">
              <Compass size={16} className="text-gold" /> Start a Bespoke Search
            </button>
          </div>
        </motion.div>

        <div className="flex items-center justify-center">
          <LogoEmblem size={380} spin className="hidden sm:block" />
          <LogoEmblem size={230} spin className="sm:hidden" />
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 font-sans text-[10px] tracking-luxe uppercase text-softgrey/40">
        {strip || "Exclusive · Global · Strategic · Discreet"}
      </div>
    </section>
  );
}
